import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseApi, ICourse } from '../api/CourseApi';
import { CourseOfferingApi, ICourseOffering } from '../api/CourseOfferingApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import CourseExerciseActivity from "./CourseExerciseActivity";
import CourseStudentActivity from './CourseStudentActivity';
import CourseStudentRoster from "./CourseStudentRoster";

interface CourseOfferingFormProps {
    courseOfferingId: number;
    onSubmit: (updatedCourseOffering: ICourseOffering) => void;
    onDelete: (dialogPlanId: number) => void;
}

const CourseOfferingForm: React.FC<CourseOfferingFormProps> = ({ courseOfferingId, onSubmit, onDelete }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [id, setId] = React.useState(-1);
    const [courseId, setCourseId] = React.useState<number>(-1);
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState<string | null>(null);
    const [displayConversationMenu, setDisplayConversationMenu] = React.useState(true);
    const [displayOfficeHourMenu, setDisplayOfficeHourMenu] = React.useState(true);
    const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
    const [endDate, setEndDate] = React.useState<Dayjs | null>(null);

    const [signupCode, setSignupCode] = React.useState<string | null>(null);

    const [showConfirmation, setShowConfirmation] = React.useState(false);

    const [courseList, setCourseList] = React.useState<ICourse[]>([]);

    const [tabValue, setTabValue] = React.useState(-1);


    React.useEffect(() => {
        const fetchCourseList = async () => {
            try {
                const response = await CourseApi.getAll();
                setCourseList(response.data);
            } catch (err: any) {
                handleErrorMessage(err, setErrorMessage, history, location);
            }
        };
        const fetchSignupCode = async () => {
            try {
                CourseOfferingApi.getSignupCode(courseOfferingId).then((response) => {
                    setSignupCode(response.data)
                });
            } catch (err: any) {
                handleErrorMessage(err, setErrorMessage, history, location);
            }
        }
        fetchCourseList();
        fetchSignupCode();
    }, [courseOfferingId, history, location, setErrorMessage]);

    React.useEffect(() => {
        CourseOfferingApi.getById(courseOfferingId)
            .then((response) => {
                const courseOffering = response.data;
                setId(courseOffering.id);
                setCourseId(courseOffering.course_id ? Number(courseOffering.course_id) : -1)
                setName(courseOffering.name);
                setDescription(courseOffering.description);
                setStartDate(courseOffering.start_date ? dayjs(courseOffering.start_date) : null)
                setEndDate(courseOffering.end_date ? dayjs(courseOffering.end_date) : null)
                setDisplayConversationMenu(courseOffering.display_conversation_menu == null ? true : courseOffering.display_conversation_menu);
                setDisplayOfficeHourMenu(courseOffering.display_office_hour_menu == null ? true : courseOffering.display_office_hour_menu);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [courseOfferingId, history, location, setErrorMessage]);

    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setId(Number(e.target.value));
    };

    const handleCourseIdChange = (e: SelectChangeEvent<string>) => {
        setCourseId(Number(e.target.value));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDescription(e.target.value);
    };

    const handleDisplayConversationMenuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayConversationMenu(e.target.checked);
    };

    const handleDisplayOfficeHourMenuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayOfficeHourMenu(e.target.checked);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updateCourseOffering: ICourseOffering = {
            id: id,
            course_id: courseId ? Number(courseId) : null,
            name: name,
            description: description,
            start_date: startDate == null ? null : startDate.toDate(),
            end_date: endDate == null ? null : endDate.toDate(),
            display_conversation_menu: displayConversationMenu,
            display_office_hour_menu: displayOfficeHourMenu
        };
        onSubmit(updateCourseOffering);
    };


    const handleDeleteConfirmation = () => {
        setShowConfirmation(true);
    };

    const handleDeleteCancel = () => {
        setShowConfirmation(false);
    };

    const handleDeleteConfirm = () => {
        setShowConfirmation(false);
        onDelete(courseOfferingId);
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

    return (<>
        <form onSubmit={handleSubmit}>
            <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
                <Typography variant="h4">
                    Edit Course Offering
                </Typography>
                <input type="hidden" value={id} onChange={handleIdChange} />

                <FormControl fullWidth>
                    <InputLabel id="course-offering-course-label">Course</InputLabel>
                    <Select
                        labelId="course-offering-course-label"
                        id="course-offering-course-select"
                        label="Course"
                        value={courseId ? String(courseId) : "-1"}
                        onChange={handleCourseIdChange}
                    >
                        <MenuItem key={-1} value="-1">Select a Course</MenuItem>
                        {courseList.flatMap((course) =>
                            <MenuItem key={course.id} value={`${course.id}`} sx={{ fontWeight: course.is_hidden ? 200 : 400, opacity: course.is_hidden ? 0.4 : 1 }}>
                                {course.is_hidden && "[Hidden]"} {course.name}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>


                <TextField
                    label="Name"
                    value={name}
                    onChange={handleNameChange}
                    required
                />
                <TextField
                    label="Description"
                    value={description}
                    onChange={handleDescriptionChange}
                    multiline
                    minRows={3}
                    maxRows={8} // allows auto-expanding up to 8 lines
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack direction="row" spacing={4}>
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            slotProps={{
                                textField: { error: false, helperText: null },
                            }}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            slotProps={{
                                textField: { error: false, helperText: null },
                            }}
                        />
                    </Stack>
                </LocalizationProvider>

                <Stack direction="row" spacing={8}>
                    <FormControlLabel
                        control={<Checkbox checked={displayConversationMenu} onChange={handleDisplayConversationMenuChange} />}
                        label="Display Conversation ChatBot"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={displayOfficeHourMenu} onChange={handleDisplayOfficeHourMenuChange} />}
                        label="Display Office Hour ChatBot"
                    />
                </Stack>

                <Stack direction="row" spacing={8}>
                    <Button type="submit" variant="contained" sx={{ flex: 1 }}>
                        Save
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirmation} sx={{ flex: 1 }}>
                        Delete
                    </Button>
                </Stack>

                {/* New users sign up at /registration; account settings live at /settings */}
                <TextField label="Sign-Up URL" disabled={true} value={`${window.location.origin}/registration?c=${signupCode}`} />

                <Dialog open={showConfirmation} onClose={handleDeleteCancel}>
                    <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Delete Course Offering</Typography></DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: '#8D8D8D' }}>Are you sure you want to delete this Course Offering?</Typography>
                        <Typography>{name}</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                        <Button onClick={handleDeleteCancel} sx={{ color: '#787878' }}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} sx={{ color: '#787878' }} autoFocus>Delete</Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </form>
        <Paper
            elevation={0}
            sx={{
                mt: 2,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
            }}
        >
            {/* Chrome-like tab strip */}
            <Box
                sx={{
                    px: 1,
                    pt: 1,
                    pb: 0,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: (t) =>
                        t.palette.mode === "light" ? "action.hover" : "background.default",
                    backgroundImage: (t) =>
                        t.palette.mode === "light"
                            ? "linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0))"
                            : "none",
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"               // even spacing (50/50)
                    TabIndicatorProps={{ style: { display: "none" } }} // hide default line
                    sx={{
                        minHeight: 48,
                        "& .MuiTabs-flexContainer": { gap: 8 },          // little space between tabs
                        "& .MuiTab-root": {
                            minHeight: 40,
                            textTransform: "none",
                            fontWeight: 500,
                            color: "text.secondary",
                            opacity: 0.85,
                            pt: 2.5,
                            borderTopLeftRadius: 14,
                            borderTopRightRadius: 14,
                            transition: "all .15s ease",
                            "&:hover": {
                                opacity: 1,
                                backgroundColor: "action.hover",
                            },
                            "&.Mui-selected": {
                                opacity: 1,
                                color: "text.primary",
                                backgroundColor: "background.paper",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                transform: "translateY(1px)",                // looks “attached” to content
                            },
                        },
                    }}
                >
                    <Tab disableRipple label="Student Roster" />
                    <Tab disableRipple label="Exercise Activity" />
                    <Tab disableRipple label="Student Activity" />
                </Tabs>
            </Box>

            {/* Content area */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    // borderTop: "1px solid",
                    // borderColor: "divider",
                }}
            >
                {tabValue === 0 && <CourseStudentRoster courseOfferingId={courseOfferingId} courseId={courseId} />}
                {tabValue === 1 && <CourseExerciseActivity courseOfferingId={courseOfferingId} courseId={courseId} />}
                {tabValue === 2 && <CourseStudentActivity courseOfferingId={courseOfferingId} courseId={courseId} />}
            </Box>
        </Paper>
    </>);
};

export default CourseOfferingForm;
